import {
  BorderStyleProps,
  MarginStyleProps,
  MarginType,
} from 'foundations-components/types';
import { TooltipProps } from 'foundations-components/transitional/components/Tooltip';
import { Color } from 'foundations-theming';

import { AVATAR_SIZES, AVATAR_TYPES, SOCIAL_MAP } from '../Constants';
import { ExternalLookupObjectRecord } from './LookupObject';
import { TextProps } from 'foundations-components/components/Text';
import { AvatarSource } from '../lib/AvatarSources';
import { resolveAvatarType } from '../lib/resolveAvatarType';

export type Size = keyof typeof AVATAR_SIZES;
export type SocialNetwork = keyof typeof SOCIAL_MAP;
export type Type = keyof typeof AVATAR_TYPES;

export const isType = (value: any): value is Type => {
  return resolveAvatarType(value) !== undefined;
};

export type AvatarBorderGradient = {
  type: 'linear-gradient' | 'angular-gradient';
  colors: string[];
};

export const isAvatarBorderGradient = (
  value: unknown
): value is AvatarBorderGradient =>
  typeof value === 'object' &&
  value !== null &&
  ((value as AvatarBorderGradient).type === 'linear-gradient' ||
    (value as AvatarBorderGradient).type === 'angular-gradient');

export type AvatarBorderStyleProps = Omit<
  Pick<BorderStyleProps, 'borderWidth' | 'borderColor' | 'borderStyle'>,
  'borderColor'
> & {
  borderColor?: Color | AvatarBorderGradient;
};

export type AvatarDataProps = {
  'data-test-id'?: string;
};

export type AvatarStyleProps = AvatarBorderStyleProps & MarginStyleProps;

export interface DisplayProps extends AvatarStyleProps, AvatarDataProps {
  showTooltip?: boolean;
  size?: Size;
  childMarginInlineStart?: MarginType;
  childTruncateLength?: number;

  children?: React.ReactNode;
  displayName?: string;
  href?: string;
  isEditable?: boolean;
  onClick?: () => void;
  isLoaded?: boolean;
  socialNetwork?: SocialNetwork;
  source?: AvatarSource | null;
  src?: string;
  tooltipPlacement?: TooltipProps['placement'];
  tooltipTitle?: string;
  type?: Type;
}

export type LookupObjectRecordProps = {
  displayName?: DisplayProps['displayName'];
  href?: DisplayProps['href'];
  isEditable?: DisplayProps['isEditable'];
  lookup?: ExternalLookupObjectRecord;
  socialNetwork?: DisplayProps['socialNetwork'];
  tooltipTitle?: DisplayProps['tooltipTitle'];

  companyId?: never;
  displayTotalAvatarCount?: never;
  domain?: never;
  email?: never;
  fileManagerKey?: never;
  hasMoreHref?: never;
  hubSpotUserEmail?: never;
  marketingEventId?: never;
  maxAvatars?: never;
  productId?: never;
  src?: never;
  totalAvatarCountColor?: never;
  truncateAvatarListInTooltip?: never;
  vid?: never;
};

export type LookupObjectRecordListProps = {
  displayTotalAvatarCount?: boolean;
  hasMoreHref?: string;
  lookup?: ExternalLookupObjectRecord[];
  maxAvatars?: number;
  totalAvatarCountColor?: TextProps['color'];
  truncateAvatarListInTooltip?: boolean;

  companyId?: never;
  displayName?: never;
  domain?: never;
  email?: never;
  fileManagerKey?: never;
  href?: never;
  hubSpotUserEmail?: never;
  isEditable?: never;
  marketingEventId?: never;
  productId?: never;
  socialNetwork?: never;
  src?: never;
  tooltipTitle?: never;
  vid?: never;
};

export type IndividualLookupObjectRecordProps = {
  companyId?: number;
  displayName?: DisplayProps['displayName'];
  domain?: string;
  email?: string;
  fileManagerKey?: string;
  href?: DisplayProps['href'];
  hubSpotUserEmail?: string;
  isEditable?: DisplayProps['isEditable'];
  marketingEventId?: number;
  productId?: number;
  socialNetwork?: DisplayProps['socialNetwork'];
  src?: DisplayProps['src'];
  tooltipTitle?: DisplayProps['tooltipTitle'];
  vid?: number;

  displayTotalAvatarCount?: never;
  hasMoreHref?: never;
  lookup?: never;
  maxAvatars?: never;
  totalAvatarCountColor?: never;
  truncateAvatarListInTooltip?: never;
};

export interface BaseAvatarProps
  extends Omit<
    DisplayProps,
    | 'displayName'
    | 'href'
    | 'isEditable'
    | 'socialNetwork'
    | 'src'
    | 'tooltipTitle'
  > {
  children?: React.ReactNode;
  portalId?: number;
}

export type WithDefaults<T extends BaseAvatarProps> = Omit<
  T,
  'size' | 'showTooltip' | 'childMarginInlineStart' | 'childTruncateLength'
> & {
  size: Size;
  showTooltip: boolean;
  childMarginInlineStart: MarginType;
  childTruncateLength: number;
};
